/**
 * Where the parties disagree, and whether the disagreement means anything.
 *
 * The ledger stores what each party said. The part worth paying for is telling
 * a SYSTEMATIC disagreement from noise. Two parties whose times differ by a few
 * minutes either way are measuring a fuzzy event slightly differently and there
 * is nothing to do about it. Two parties where one is consistently fifteen
 * minutes later than the other, on the same stand, ninety times running, have a
 * process defect — and it is worth exactly the delay it causes.
 *
 * THREE RULES, and a screen must not break any of them:
 *
 *   Nothing is averaged. The mean of a handler's time and an airline's time is
 *   a number neither asserted and neither will defend. What is reported is the
 *   spread, the median signed difference, and how consistently it points one
 *   way.
 *
 *   Only one party reporting is not agreement. It is the commonest state in a
 *   real ledger and the easiest to misread as consensus, so it is its own
 *   outcome rather than folded into "no disagreement".
 *
 *   No winner is picked unless somebody agreed one. Where an authority is on
 *   file, that party's value is authoritative with its basis quoted. Where it
 *   is not, the spread is reported and the screen says nobody has agreed.
 */

export interface FactSummary {
  factKey: string;
  assertions: number;
  parties: number;
  partyKeys: string[];
  subjects: number;
  /** Subjects where more than one party reported — the only comparable ones. */
  comparableSubjects: number;
  latest?: string;
  /** The party whose value is authoritative, if anyone agreed one. */
  authority?: string;
}

export interface Divergence {
  fact: string;
  partyA: string;
  partyB: string;
  /** False when there is nothing to compare — not agreement. */
  comparable: boolean;
  whyNot?: string;
  pairs: number;
  medianSeconds?: number;
  medianMinutes?: number;
  iqrSeconds?: number;
  bLaterPct?: number;
  /** Whether the difference points one way consistently enough to act on. */
  systematic: boolean;
  /** The platform's own sentence. It is the finding. */
  finding?: string;
}

export type DivergenceState = "systematic" | "noise" | "not-comparable";

/**
 * Which of the three outcomes this is.
 *
 * `comparable` is read first. A pair with nothing in common has no
 * disagreement to report, and rendering that as "no systematic difference"
 * would turn an absence of evidence into evidence of agreement — the mistake
 * the module names explicitly.
 */
export function divergenceState(d: Divergence): DivergenceState {
  if (!d.comparable) return "not-comparable";
  return d.systematic ? "systematic" : "noise";
}

export const DIVERGENCE_LABEL: Record<DivergenceState, string> = {
  systematic: "A process difference",
  noise: "Measurement noise",
  "not-comparable": "Nothing to compare",
};

/**
 * Whether anyone has agreed who is right on this fact.
 *
 * Absence of an authority is a real finding on a busy fact: two parties
 * asserting different times for eighteen subjects with nobody nominated to
 * settle it is a contract gap, not a data gap.
 */
export function hasAuthority(f: FactSummary): boolean {
  return !!f.authority;
}

/** Facts where parties actually overlap, busiest first — the rest cannot diverge. */
export function comparableFirst(facts: FactSummary[]): FactSummary[] {
  return [...facts].sort(
    (a, b) => b.comparableSubjects - a.comparableSubjects || b.assertions - a.assertions,
  );
}
